import { ExclamationCircleOutlined } from '@ant-design/icons';
import { message, Tooltip } from 'antd';
import confirm from 'antd/lib/modal/confirm';
import moment from 'moment';
import { useEffect, useMemo } from 'react';
import Flag from 'react-flagpack';

import {
  Alert,
  Button,
  Checkbox,
  Descriptions,
  Drawer,
  ErrorBoundary,
  FormatItem,
  GenericDrawerHeader,
  SlimTable,
} from '../../../../../components/index';
import { usePatchFareMutation } from '../../../../../services/fareApi';
import {
  AllTableItemsType,
  AllTableType,
  ChargeType,
  FareType,
  TableType,
} from '../../../../../typesFinancial';
import { chargeTypeList, rmMovements } from '../../../../../utils/lists';
import {
  createDateStringPtBr,
  createDateStringPtBrSeconds,
  formatNumberToLocale,
  returnDiffDaysInHours,
} from '../../../../../utils/utils';
import { isExemptionConfirmed } from '../../exemption';
import { canSendFare } from '../FaresTablesData';
import { RmTransmissionSection } from './RmTransmissionSection';
import { Container, Title, Section, SectionCard } from './styles';

const STATEMENT_TABLES: [string, TableType][] = [
  ['TAB. 1', 'table_one'],
  ['TAB. 2', 'table_two'],
  ['TAB. 3', 'table_three'],
  ['TAB. 5', 'table_five'],
  ['TAB. 6', 'table_six'],
  ['TAB. 7', 'table_seven'],
  ['TAB. 9', 'table_nine'],
];

type FareStatementDrawerProps = {
  isVisible: boolean;
  setIsVisible: (value: boolean) => void;
  selectedFare: FareType;
  selectedCharge: ChargeType;
};

type ServicesDataSource = {
  id: number;
  description: string;
  value?: string;
  gross_value?: string;
  discount_value?: string;
  segregated_billing?: boolean;
  raw_value?: number;
  raw_discount?: number;
  exempt?: boolean;
};

type ExemptionDataSource = {
  id: number;
  label: string;
  table: string;
  description: string;
};

type DiscountDataSource = {
  id: number;
  table: string;
  name: string;
  percentual?: number;
  value: number;
};

const { Item } = Descriptions;

export function FareStatementDrawer(props: FareStatementDrawerProps) {
  const { isVisible, setIsVisible, selectedFare, selectedCharge } = props;

  const isVesselType = selectedCharge.charge_type === 'VESSEL';
  const isFareExempt = !!selectedFare.is_exempt;
  const isExemptionPending =
    isFareExempt && !isExemptionConfirmed(selectedFare.status);

  function onClose() {
    setIsVisible(false);
  }

  const chargeType = useMemo(() => {
    return (
      chargeTypeList.find((item) => item.id === selectedCharge.charge_type) || {
        id: '',
        name: '',
      }
    );
  }, [selectedCharge]);

  const vesselInfo = useMemo(() => {
    if (chargeType.id === 'VESSEL') {
      return {
        name: selectedCharge.vessel?.name,
        flag: selectedCharge.vessel?.flag,
      };
    }
    if (chargeType.id === 'DOCKING') {
      return {
        name: selectedCharge.stopover?.vessel_name,
        flag: selectedCharge.stopover?.vessel_name,
      };
    }
    return { name: '', flag: '' };
  }, [chargeType]);

  const [
    patchFare,
    {
      isSuccess: isSuccessPatchFare,
      isLoading: isLoadingPatchFare,
      originalArgs: patchFareArgs,
      reset: resetPatchFare,
    },
  ] = usePatchFareMutation();

  // O drawer fica montado com o painel, então o resultado da mutation sobrevive
  // ao fechamento: sem zerar, as ações de envio abririam desabilitadas na
  // próxima tarifa.
  useEffect(() => {
    if (!isVisible) resetPatchFare();
  }, [isVisible]);

  const dockingDuration = useMemo(() => {
    if (
      !selectedCharge.docking?.first_cable_tied_in ||
      !selectedCharge.docking?.last_cable_untied_in
    ) {
      return '00h 00';
    }
    return returnDiffDaysInHours(
      selectedCharge.docking?.first_cable_tied_in || '',
      selectedCharge.docking?.last_cable_untied_in || ''
    );
  }, [selectedCharge]);

  function getCopyText() {
    let text = `\n\n\n\n\n\n\n\n\n\n\nARMADOR: \n${
      selectedCharge.vessel?.shipowner?.name ?? ''
    } C.N.P.J/C.P.F. ${
      selectedFare?.company?.identifier ?? ''
    } \nOPERADOR: \n Código: ${selectedFare?.company?.id ?? ''}  Nome: ${
      selectedFare?.company?.name ?? ''
    }  C.N.P.J/C.P.F.:${
      selectedFare?.company?.cnpj ?? ''
    }\nNOTAFISCAL:\nCódigo:${''}  Nome: ${
      selectedCharge.company?.name ?? ''
    } Cod.Contábil\nEndereço: ${
      selectedFare?.company?.address?.address ?? ''
    } \nBairro: ${selectedFare?.company?.address?.neighborhood ?? ''} Cidade: ${
      selectedFare?.company?.address?.city ?? ''
    } Estado: ${selectedFare?.company?.address?.state ?? ''} CEP: ${
      selectedFare?.company?.address?.postal_code ?? ''
    } \nC.N.P.J/C.P.F. ${
      selectedFare?.company?.identifier ?? ''
    } I.E.: ${''} I.M.: ${''}\nNAVIO/ATRACAÇÃO\nI.M.O: ${
      isVesselType
        ? selectedCharge.vessel?.imo
        : selectedCharge?.stopover?.vessel_imo
    } Nome: ${
      isVesselType
        ? selectedCharge?.vessel?.name
        : selectedCharge?.stopover?.vessel_name
    }\nCalado: ${''} Loa: ${selectedCharge?.vessel?.loa ?? ''} DWT: ${
      selectedCharge?.docking?.input_dwt || selectedCharge?.vessel?.dwt
    } Arqueação: ${''}\n\nViagem: ${
      selectedCharge?.stopover?.shipowner_trip ?? ''
    } Atracação: ${selectedCharge?.docking?.code ?? ''} \nBerço: ${
      selectedCharge?.docking?.docking_place_name ?? ''
    } Programação: ${''} Cesv: ${''}\nData Atracação: ${createDateStringPtBrSeconds(
      selectedCharge?.docking?.first_cable_tied_in ?? ''
    )} Data Desatracação: ${createDateStringPtBrSeconds(
      selectedCharge?.docking?.last_cable_untied_in ?? ''
    )}\n\n\n\n\n`;

    if (selectedFare.table_one && selectedFare.table_one.items) {
      selectedFare.table_one.items.forEach((table_one_item) => {
        text = text.concat(
          `\nTAB. 1 - ${table_one_item.number}: ${
            table_one_item.name
          } ${copyItemValue(selectedFare.table_one, table_one_item.value)} ${
            table_one_item.minimal_value_applied ? 'Valor mínimo aplicado' : ''
          }`
        );
      });
    }

    if (selectedFare.table_two && selectedFare.table_two.items) {
      selectedFare.table_two.items.forEach((table_two_item) => {
        text = text.concat(
          `\nTAB. 2 - ${table_two_item.number}: ${
            table_two_item.name
          } ${copyItemValue(selectedFare.table_two, table_two_item.value)} ${
            table_two_item.minimal_value_applied ? 'Valor mínimo aplicado' : ''
          }`
        );
      });
    }

    if (selectedFare.table_three && selectedFare.table_three.items) {
      selectedFare.table_three.items.forEach((table_three_item) => {
        text = text.concat(
          `\nTAB. 3 - ${table_three_item.number}: ${
            table_three_item.name
          } ${copyItemValue(
            selectedFare.table_three,
            table_three_item.value
          )} ${
            table_three_item.minimal_value_applied
              ? 'Valor mínimo aplicado'
              : ''
          }`
        );
      });
    }

    if (selectedFare.table_five && selectedFare.table_five.items) {
      selectedFare.table_five.items.forEach((table_five_item) => {
        text = text.concat(
          `\nTAB. 5 - ${table_five_item.number}: ${
            table_five_item.name
          } ${copyItemValue(selectedFare.table_five, table_five_item.value)} ${
            table_five_item.minimal_value_applied ? 'Valor mínimo aplicado' : ''
          }`
        );
      });
    }

    if (selectedFare.table_six && selectedFare.table_six.items) {
      selectedFare.table_six.items.forEach((table_six_item) => {
        text = text.concat(
          `\nTAB. 6 - ${table_six_item.number}: ${
            table_six_item.name
          } ${copyItemValue(selectedFare.table_six, table_six_item.value)} ${
            table_six_item.minimal_value_applied ? 'Valor mínimo aplicado' : ''
          }`
        );
      });
    }

    if (selectedFare.table_seven && selectedFare.table_seven.items) {
      selectedFare.table_seven.items.forEach((table_seven_item) => {
        text = text.concat(
          `\nTAB. 7 - ${table_seven_item.number}: ${
            table_seven_item.name
          } ${copyItemValue(
            selectedFare.table_seven,
            table_seven_item.value
          )} ${
            table_seven_item.minimal_value_applied
              ? 'Valor mínimo aplicado'
              : ''
          }`
        );
      });
    }

    if (selectedFare.table_nine && selectedFare.table_nine.items) {
      selectedFare.table_nine.items.forEach((table_nine_item) => {
        text = text.concat(
          `\nTAB. 9 - ${table_nine_item.number}: ${
            table_nine_item.name
          } ${copyItemValue(selectedFare.table_nine, table_nine_item.value)} ${
            table_nine_item.minimal_value_applied ? 'Valor mínimo aplicado' : ''
          }`
        );
      });
    }

    if (
      selectedFare.table_one &&
      selectedFare.table_one.discount &&
      selectedFare.table_one.discount.items
    ) {
      selectedFare.table_one.discount.items.forEach((discount) => {
        text = text.concat(
          `\nDesconto: ${
            formatNumberToLocale(discount.value) || ''
          } concedido na Tab.1 referente à ${discount.name}.`
        );
      });
    }

    if (
      selectedFare.table_two &&
      selectedFare.table_two.discount &&
      selectedFare.table_two.discount.items
    ) {
      selectedFare.table_two.discount.items.forEach((discount) => {
        text = text.concat(
          `\nDesconto: ${
            formatNumberToLocale(discount.value) || ''
          } concedido na Tab.2 referente à ${discount.name}.`
        );
      });
    }

    if (
      selectedFare.table_three &&
      selectedFare.table_three.discount &&
      selectedFare.table_three.discount.items
    ) {
      selectedFare.table_three.discount.items.forEach((discount) => {
        text = text.concat(
          `\nDesconto: ${
            formatNumberToLocale(discount.value) || ''
          } concedido na Tab.3 referente à ${discount.name}.`
        );
      });
    }

    if (
      selectedFare.table_five &&
      selectedFare.table_five.discount &&
      selectedFare.table_five.discount.items
    ) {
      selectedFare.table_five.discount.items.forEach((discount) => {
        text = text.concat(
          `\nDesconto: ${
            formatNumberToLocale(discount.value) || ''
          } concedido na Tab.5 referente à ${discount.name}.`
        );
      });
    }

    if (
      selectedFare.table_six &&
      selectedFare.table_six.discount &&
      selectedFare.table_six.discount.items
    ) {
      selectedFare.table_six.discount.items.forEach((discount) => {
        text = text.concat(
          `\nDesconto: ${
            formatNumberToLocale(discount.value) || ''
          } concedido na Tab.6 referente à ${discount.name}.`
        );
      });
    }

    if (
      selectedFare.table_seven &&
      selectedFare.table_seven.discount &&
      selectedFare.table_seven.discount.items
    ) {
      selectedFare.table_seven.discount.items.forEach((discount) => {
        text = text.concat(
          `\nDesconto: ${
            formatNumberToLocale(discount.value) || ''
          } concedido na Tab.7 referente à ${discount.name}.`
        );
      });
    }

    if (
      selectedFare.table_nine &&
      selectedFare.table_nine.discount &&
      selectedFare.table_nine.discount.items
    ) {
      selectedFare.table_nine.discount.items.forEach((discount) => {
        text = text.concat(
          `\nDesconto: ${
            formatNumberToLocale(discount.value) || ''
          } concedido na Tab.9 referente à ${discount.name}.`
        );
      });
    }
    getExemptionsDatasource().forEach((exemption) => {
      text = text.concat(
        `\nISENÇÃO ${exemption.label}: ${exemption.description}`
      );
    });
    return text;
  }

  function copyItemValue(table?: AllTableType, value?: number) {
    if (table?.exemption) {
      return 'ISENTO';
    }
    return `R$ ${formatNumberToLocale(value) || ''}`;
  }

  function copyStatement() {
    return navigator.clipboard.writeText(getCopyText());
  }

  // O envio ao RM é assíncrono: a resposta 200 significa que a transmissão foi
  // enfileirada, não que o RM aceitou. O desfecho aparece depois, no status da
  // tarifa.
  useEffect(() => {
    if (isSuccessPatchFare)
      message.success(
        isFareExempt
          ? 'Isenção confirmada. A tarifa será concluída sem envio ao RM.'
          : patchFareArgs?.isSubstitution
          ? 'Envio como substituição iniciado.'
          : 'Envio para o pré-faturamento iniciado.'
      );
  }, [isSuccessPatchFare]);

  // As duas ações sempre informam o destino, para a tela não depender do
  // padrão do backend.
  function sendFare(isSubstitution: boolean) {
    if (selectedFare.id && selectedCharge.id)
      patchFare({
        action: 'generate_statement',
        id: selectedFare.id,
        chargeId: selectedCharge.id,
        isSubstitution,
      });
  }

  // Arquivamento parcial: tarifa arquivada convivendo com tarifa ativa na mesma
  // cobrança. O envio leva só as ativas, e nada na tela dizia isso. A contagem
  // já ignora as complementares sem precisar filtrar, porque `charge.fare` vem
  // sem elas (as complementares chegam aninhadas em `fare.complementaries`),
  // que é a mesma regra que decide o status da atracação.
  //
  // Avisa, e não bloqueia: cobrar ou não as arquivadas é decisão do faturista.
  // O caminho para cobrá-las é arquivar a cobrança inteira e gerar outra,
  // porque tarifa arquivada não volta para o lote desta.
  function getArchivedFareWarning() {
    const chargeFares = selectedCharge?.fare || [];
    const archivedFareCount = chargeFares.filter(
      (fare) => fare.status === 'ARCHIVED'
    ).length;
    const isPartiallyArchived =
      archivedFareCount > 0 && archivedFareCount < chargeFares.length;

    return isPartiallyArchived
      ? `${archivedFareCount} de ${chargeFares.length} tarifas desta cobrança estão arquivadas e não entram neste envio. Se elas também devem ser cobradas, arquive a cobrança inteira e gere uma nova no lugar desta.`
      : undefined;
  }

  function handleSendToPreInvoicing() {
    const archivedFareWarning = getArchivedFareWarning();

    if (archivedFareWarning) {
      confirm({
        title: 'Esta cobrança tem tarifa arquivada',
        icon: <ExclamationCircleOutlined />,
        content: archivedFareWarning,
        okText: isFareExempt
          ? 'Confirmar sem as arquivadas'
          : 'Enviar sem as arquivadas',
        cancelText: 'Cancelar',
        onOk() {
          sendFare(false);
        },
      });
      return;
    }

    sendFare(false);
  }

  // Substituição é exceção, e mandada sem nota a substituir vira exclusão no
  // RM, então ela confirma antes e o pré-faturamento não. A confirmação diz o efeito em
  // vez de perguntar se o faturista tem certeza, e leva junto o aviso de
  // arquivadas para não serem dois modais seguidos.
  function handleSendAsSubstitution() {
    const archivedFareWarning = getArchivedFareWarning();

    confirm({
      title: 'Enviar como substituição de nota',
      icon: <ExclamationCircleOutlined />,
      content: (
        <>
          <p>
            A tarifa vai para o movimento {rmMovements.substitution.code} do
            TOTVS RM, com todas as suas movimentações, em vez do pré-faturamento
            ({rmMovements.preInvoicing.code}). Lá, o faturamento vincula o RPS
            que está sendo substituído.
          </p>
          {archivedFareWarning && (
            <p>
              <strong>Esta cobrança tem tarifa arquivada.</strong>{' '}
              {archivedFareWarning}
            </p>
          )}
        </>
      ),
      okText: 'Enviar como substituição',
      cancelText: 'Cancelar',
      onOk() {
        sendFare(true);
      },
    });
  }

  // Desabilitadas depois do sucesso porque a tarifa já está em PROCESSING e o
  // backend recusaria um segundo envio. Durante a requisição, o carregamento
  // fica no botão clicado e o outro fica desabilitado. O drawer segue aberto de
  // propósito, para o "Copiar" continuar à mão.
  function getSendButtonState(isSubstitution: boolean) {
    const isThisSend = patchFareArgs?.isSubstitution === isSubstitution;

    return {
      loading: isLoadingPatchFare && isThisSend,
      disabled: isSuccessPatchFare || (isLoadingPatchFare && !isThisSend),
    };
  }

  function getTitle() {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <ErrorBoundary replace="">
            <Flag code={vesselInfo.flag} hasBorder={false} size="l" />
          </ErrorBoundary>
          <span
            className="title"
            style={{ paddingLeft: '8px', fontSize: '16px' }}
          >
            <strong>{vesselInfo.name}</strong>
          </span>
          {chargeType.id === 'DOCKING' && (
            <span
              style={{
                color: 'var(--neutral_light)',
                paddingLeft: '8px',
                fontFamily: 'Consolas',
              }}
            >
              ATR: {selectedCharge.docking?.code}
            </span>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            gap: '20px',
          }}
        >
          <Button type="dashed" onClick={() => copyStatement()}>
            Copiar
          </Button>
          {canSendFare(selectedFare) &&
            (isFareExempt ? (
              <Button
                type="primary"
                onClick={() => handleSendToPreInvoicing()}
                {...getSendButtonState(false)}
              >
                Confirmar isenção
              </Button>
            ) : (
              <>
                <Tooltip
                  title={`Movimento ${rmMovements.substitution.code} do TOTVS RM, para substituir uma nota já emitida`}
                >
                  <Button
                    onClick={() => handleSendAsSubstitution()}
                    {...getSendButtonState(true)}
                  >
                    Enviar como substituição
                  </Button>
                </Tooltip>
                <Tooltip
                  title={`Movimento ${rmMovements.preInvoicing.code} do TOTVS RM`}
                >
                  <Button
                    type="primary"
                    onClick={() => handleSendToPreInvoicing()}
                    {...getSendButtonState(false)}
                  >
                    Enviar para pré-faturamento
                  </Button>
                </Tooltip>
              </>
            ))}
        </div>
      </div>
    );
  }

  function discountOfItem(table: AllTableType, itemId?: number) {
    return (
      (table.discount_by_item || []).find(
        (discount) => discount.item_id === itemId
      )?.value || 0
    );
  }

  function getServicesDatasource() {
    const data: ServicesDataSource[] = [];
    let index = 1;

    STATEMENT_TABLES.forEach(([label, tableName]) => {
      const table = selectedFare[tableName] as AllTableType | undefined;

      table?.items?.forEach((item: AllTableItemsType) => {
        if (table.exemption) {
          data.push({
            id: index,
            description: `${label} - ${item.name}`,
            segregated_billing: item.segregated_billing,
            gross_value: '',
            discount_value: '',
            value: 'Isento',
            raw_value: 0,
            raw_discount: 0,
            exempt: true,
          });
          index++;
          return;
        }

        const grossValue = item.value || 0;
        const discountValue = discountOfItem(table, item.id);
        const itemValue = grossValue - discountValue;

        data.push({
          id: index,
          description: `${label} - ${item.name}`,
          segregated_billing: item.segregated_billing,
          gross_value: formatNumberToLocale(grossValue, true) || '',
          discount_value: discountValue
            ? `- ${formatNumberToLocale(discountValue, true)}`
            : '',
          value: formatNumberToLocale(itemValue, true) || '',
          raw_value: itemValue,
          raw_discount: discountValue,
        });

        index++;
      });
    });

    return data;
  }

  function getExemptionsDatasource() {
    const data: ExemptionDataSource[] = [];

    STATEMENT_TABLES.forEach(([label, tableName]) => {
      const table = selectedFare[tableName] as AllTableType | undefined;

      if (table?.exemption) {
        data.push({
          id: data.length + 1,
          label,
          table: label.replace('TAB.', 'Tabela'),
          description: table.exemption.description,
        });
      }
    });

    return data;
  }

  function getDiscountsDatasource() {
    const data: DiscountDataSource[] = [];
    let index = 1;

    STATEMENT_TABLES.forEach(([label, tableName]) => {
      const table = selectedFare[tableName] as AllTableType | undefined;

      table?.discount?.items?.forEach((discount) => {
        if (!discount.value) {
          return;
        }

        data.push({
          id: index,
          table: label,
          name: discount.name,
          percentual: discount.percentual,
          value: discount.value,
        });

        index++;
      });
    });

    return data;
  }

  function renderSegregatedBilling(value: any) {
    if (value === true) {
      return <Checkbox checked disabled />;
    }
    return <Checkbox checked={false} disabled />;
  }

  function renderMonospace(value: string) {
    return <span style={{ fontFamily: 'Consolas' }}>{value}</span>;
  }

  function renderServiceValue(
    value: string,
    record: Pick<ServicesDataSource, 'exempt'>
  ) {
    if (record.exempt) {
      return <span style={{ color: 'var(--neutral_medium)' }}>{value}</span>;
    }
    return renderMonospace(value);
  }

  function getFooterForServices() {
    const values = getServicesDatasource();
    const totalValue = values.reduce(
      (acc, obj) => acc + (obj.raw_value || 0),
      0
    );
    const totalDiscount = values.reduce(
      (acc, obj) => acc + (obj.raw_discount || 0),
      0
    );

    return (
      <div
        style={{
          textAlign: 'right',
          color: '#2E3439',
          fontSize: '16px',
          margin: '0',
          fontFamily: 'Consolas',
        }}
      >
        {totalDiscount > 0 && (
          <>
            <p style={{ margin: '0', fontSize: '14px' }}>
              SOMA DOS SERVIÇOS:{' '}
              {formatNumberToLocale(totalValue + totalDiscount, true)}
            </p>
            <p style={{ margin: '0', fontSize: '14px' }}>
              DESCONTOS: - {formatNumberToLocale(totalDiscount, true)}
            </p>
          </>
        )}
        <p style={{ margin: '0' }}>
          TOTAL DO DEMONSTRATIVO: {formatNumberToLocale(totalValue, true)}
        </p>
      </div>
    );
  }

  const hasDiscount = getDiscountsDatasource().length > 0;
  const exemptions = getExemptionsDatasource();

  return (
    <Drawer
      visible={isVisible}
      placement="right"
      width={1200}
      closable={false}
      destroyOnClose
      onClose={onClose}
    >
      <GenericDrawerHeader title={getTitle()} showBackButton onBack={onClose} />
      <Container>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            marginTop: '18px',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div>
              <Title>Tipo de faturamento: </Title>
              <span>{chargeType?.name}</span>
            </div>
            <p className="date">
              Data: {moment().format('DD/MM/YYYY - HH:mm')}
            </p>
          </div>
          <Title>Demonstrativo de cálculo</Title>
        </div>

        {isFareExempt && (
          <Alert
            type={isExemptionPending ? 'warning' : 'info'}
            showIcon
            style={{ marginTop: '16px' }}
            message={
              isExemptionPending
                ? 'Esta tarifa está isenta e não será enviada ao RM'
                : 'Esta tarifa está isenta e foi concluída sem envio ao RM'
            }
            description={
              <>
                {exemptions.map((exemption) => (
                  <div key={exemption.id}>
                    {exemption.table}: {exemption.description}.
                  </div>
                ))}
                {isExemptionPending && (
                  <div>
                    Ao confirmar, a tarifa fica como faturada, sem valor a
                    cobrar. Se a isenção não vale para esta atracação, não
                    confirme e verifique o cadastro da embarcação.
                  </div>
                )}
              </>
            }
          />
        )}

        <Section>
          <span className="section-title">{selectedFare?.company?.name}</span>
          <SectionCard>
            <Descriptions column={5}>
              <Item label="Endereço">
                <FormatItem style={{ fontFamily: 'Consolas' }}>
                  {selectedFare?.company?.address?.address}
                </FormatItem>
              </Item>
              <Item label="Código">
                <FormatItem style={{ fontFamily: 'Consolas' }}>
                  {null}
                </FormatItem>
              </Item>
              <Item label="CEP">
                <FormatItem style={{ fontFamily: 'Consolas' }}>
                  {selectedFare?.company?.address?.postal_code}
                </FormatItem>
              </Item>
              <Item label="CNPJ/CPF">
                <FormatItem style={{ fontFamily: 'Consolas' }}>
                  {selectedFare?.company?.identifier}
                </FormatItem>
              </Item>
              <Item label="Cidade">
                <FormatItem style={{ fontFamily: 'Consolas' }}>
                  {selectedFare?.company?.address?.city}
                </FormatItem>
              </Item>
              <Item label="Cidade">
                <FormatItem style={{ fontFamily: 'Consolas' }}>
                  {selectedFare?.company?.address?.city}
                </FormatItem>
              </Item>
              <Item label="Bairro">
                <FormatItem style={{ fontFamily: 'Consolas' }}>
                  {selectedFare?.company?.address?.neighborhood}
                </FormatItem>
              </Item>
              <Item label="Estado">
                <FormatItem style={{ fontFamily: 'Consolas' }}>
                  {selectedFare?.company?.address?.state}
                </FormatItem>
              </Item>
              <Item label="I.E">
                <FormatItem style={{ fontFamily: 'Consolas' }}>
                  {null}
                </FormatItem>
              </Item>
              <Item label="I.M">
                <FormatItem style={{ fontFamily: 'Consolas' }}>
                  {null}
                </FormatItem>
              </Item>
            </Descriptions>
          </SectionCard>
        </Section>
        {chargeType.id !== 'SINGLE_FARE' && (
          <Section>
            <span className="section-title">EMBARCAÇÃO/ATRACAÇÃO</span>
            <SectionCard>
              <Descriptions column={5}>
                <Item label="Embarcação">
                  <FormatItem style={{ fontFamily: 'Consolas' }}>
                    {isVesselType
                      ? selectedCharge?.vessel?.name
                      : selectedCharge?.stopover?.vessel_name}
                  </FormatItem>
                </Item>
                <Item label="Cód. Atracação">
                  <FormatItem style={{ fontFamily: 'Consolas' }}>
                    {selectedCharge?.docking?.code}
                  </FormatItem>
                </Item>
                <Item label="Viagem">
                  <FormatItem style={{ fontFamily: 'Consolas' }}>
                    {selectedCharge?.stopover?.shipowner_trip}
                  </FormatItem>
                </Item>
                <Item label="Berço">
                  <FormatItem style={{ fontFamily: 'Consolas' }}>
                    {selectedCharge?.docking?.docking_place_name}
                  </FormatItem>
                </Item>
                <Item label="IMO">
                  <FormatItem style={{ fontFamily: 'Consolas' }}>
                    {isVesselType
                      ? selectedCharge.vessel?.imo
                      : selectedCharge?.stopover?.vessel_imo}
                  </FormatItem>
                </Item>
                <Item label="Chegada">
                  <FormatItem style={{ fontFamily: 'Consolas' }}>
                    {createDateStringPtBr(
                      selectedCharge?.stopover?.actual_time_of_arrival
                    )}
                  </FormatItem>
                </Item>
                <Item label="Atracação">
                  <FormatItem style={{ fontFamily: 'Consolas' }}>
                    {createDateStringPtBr(
                      selectedCharge?.docking?.first_cable_tied_in
                    )}
                  </FormatItem>
                </Item>
                <Item label="Inicio operação">
                  <FormatItem style={{ fontFamily: 'Consolas' }}>
                    {createDateStringPtBr(
                      selectedCharge?.docking?.real_operation_start
                    )}
                  </FormatItem>
                </Item>
                <Item label="LOA">
                  <FormatItem style={{ fontFamily: 'Consolas' }}>
                    {isVesselType
                      ? selectedCharge?.vessel?.loa
                      : selectedCharge?.stopover?.vessel_loa}
                  </FormatItem>
                </Item>
                <Item label="DWT">
                  <FormatItem style={{ fontFamily: 'Consolas' }}>
                    {selectedCharge?.docking?.input_dwt ||
                      selectedCharge?.vessel?.dwt}
                  </FormatItem>
                </Item>
                <Item label="Saída">
                  <FormatItem style={{ fontFamily: 'Consolas' }}>
                    {createDateStringPtBr(
                      selectedCharge?.stopover?.port_stay_finished
                    )}
                  </FormatItem>
                </Item>
                <Item label="Desatracação">
                  <FormatItem style={{ fontFamily: 'Consolas' }}>
                    {createDateStringPtBr(
                      selectedCharge?.docking?.last_cable_untied_in
                    )}
                  </FormatItem>
                </Item>
                <Item label="Fim operação">
                  <FormatItem style={{ fontFamily: 'Consolas' }}>
                    {createDateStringPtBr(
                      selectedCharge?.docking?.real_operation_finish
                    )}
                  </FormatItem>
                </Item>
              </Descriptions>
            </SectionCard>
          </Section>
        )}
        <RmTransmissionSection fare={selectedFare} charge={selectedCharge} />
        <Section
          style={{
            background: '#ffffff ',
            borderWidth: '0px 1px 1px 1px',
            borderStyle: 'solid',
            borderColor: '#CCDDE9',
          }}
        >
          <span className="section-title">Serviços</span>

          <SlimTable
            rowKey="id"
            columns={[
              {
                title: 'Itens',
                dataIndex: 'id',
                key: 'items',
                width: '5%',
              },
              {
                title: 'Descrição',
                dataIndex: 'description',
                key: 'description',
                width: hasDiscount ? '44%' : '60%',
              },
              {
                title: 'Faturamento Separado',
                dataIndex: 'segregated_billing',
                key: 'segregated_billing',
                width: hasDiscount ? '12%' : '15%',
                align: 'right',
                render: renderSegregatedBilling,
              },
              ...(hasDiscount
                ? [
                    {
                      title: 'Valor bruto',
                      dataIndex: 'gross_value',
                      key: 'gross_value',
                      align: 'right' as const,
                      width: '13%',
                      render: renderServiceValue,
                    },
                    {
                      title: 'Desconto',
                      dataIndex: 'discount_value',
                      key: 'discount_value',
                      align: 'right' as const,
                      width: '13%',
                      render: renderServiceValue,
                    },
                  ]
                : []),
              {
                title: 'Valor Total',
                dataIndex: 'value',
                key: 'value',
                align: 'right',
                width: hasDiscount ? '13%' : '20%',
                render: renderServiceValue,
              },
            ]}
            dataSource={getServicesDatasource()}
            pagination={false}
            footer={() => getFooterForServices()}
          />
        </Section>

        {hasDiscount && (
          <Section
            style={{
              background: '#ffffff ',
              borderWidth: '0px 1px 1px 1px',
              borderStyle: 'solid',
              borderColor: '#CCDDE9',
            }}
          >
            <span className="section-title">Descontos concedidos</span>

            <SlimTable
              rowKey="id"
              columns={[
                {
                  title: 'Tabela',
                  dataIndex: 'table',
                  key: 'table',
                  width: '10%',
                },
                {
                  title: 'Descrição',
                  dataIndex: 'name',
                  key: 'name',
                  width: '61%',
                },
                {
                  title: 'Percentual',
                  dataIndex: 'percentual',
                  key: 'percentual',
                  align: 'right',
                  width: '14%',
                  render(value) {
                    return renderMonospace(
                      `${formatNumberToLocale(value) || '0'}%`
                    );
                  },
                },
                {
                  title: 'Valor',
                  dataIndex: 'value',
                  key: 'value',
                  align: 'right',
                  width: '15%',
                  render(value) {
                    return renderMonospace(
                      `- ${formatNumberToLocale(value, true)}`
                    );
                  },
                },
              ]}
              dataSource={getDiscountsDatasource()}
              pagination={false}
            />
          </Section>
        )}

        {exemptions.length > 0 && (
          <Section
            style={{
              background: '#ffffff ',
              borderWidth: '0px 1px 1px 1px',
              borderStyle: 'solid',
              borderColor: '#CCDDE9',
            }}
          >
            <span className="section-title">Isenções aplicadas</span>

            <SlimTable
              rowKey="id"
              columns={[
                {
                  title: 'Tabela',
                  dataIndex: 'table',
                  key: 'table',
                  width: '10%',
                },
                {
                  title: 'Motivo',
                  dataIndex: 'description',
                  key: 'description',
                  width: '90%',
                },
              ]}
              dataSource={exemptions}
              pagination={false}
            />
          </Section>
        )}
      </Container>
    </Drawer>
  );
}
